import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sailboat,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Vietnam DMC", path: "/vietnam-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "Incentive Travel",
    copy:
      "Themed gala dinners in restored Hanoi colonial villas, junk-boat overnights on Ha Long Bay, rooftop welcome parties in Saigon's CBD.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "End-to-end programme delivery across Hanoi, Ho Chi Minh City and Da Nang — flights, hotels, transfers, F&B, and dedicated English-speaking guides.",
  },
  {
    icon: Trophy,
    title: "MICE & Conferences",
    copy:
      "Full conference production at the National Convention Centre Hanoi, SECC HCMC, and luxury resort venues in Da Nang and Phu Quoc.",
  },
  {
    icon: Sailboat,
    title: "Cruise Programmes",
    copy:
      "Private junk-boat charters and luxury cruise buyouts on Ha Long Bay and Lan Ha Bay — for groups of 12 to 200 pax with on-board catering and entertainment.",
  },
];

const CITIES = [
  {
    name: "Hanoi",
    note:
      "The cultural capital. French colonial heritage, Old Quarter charm, and luxury 5-star anchors like the Sofitel Metropole and Capella. Best for incentive groups with a cultural storyline.",
  },
  {
    name: "Ho Chi Minh City",
    note:
      "Vietnam's commercial engine. Park Hyatt, Reverie Saigon and The Reverie offer top-tier inventory. Excellent for corporate workshops, mid-sized conferences and rooftop incentive evenings.",
  },
  {
    name: "Ha Long Bay & Da Nang",
    note:
      "Beach incentives and cruise programmes. Ha Long's UNESCO-listed karst scenery is unmatched. Da Nang offers the InterContinental Sun Peninsula and Four Seasons — both world-class.",
  },
];

const VENUES = [
  { name: "National Convention Centre Hanoi", note: "3,800-seat plenary hall — Vietnam's largest conference venue" },
  { name: "Saigon Exhibition & Convention Centre (SECC)", note: "30,000 sqm exhibition halls in HCMC" },
  { name: "Sofitel Legend Metropole Hanoi", note: "Historic 5-star colonial hotel — top choice for premium incentives" },
  { name: "InterContinental Danang Sun Peninsula", note: "Bill Bensley-designed luxury resort for beach buyouts" },
  { name: "Park Hyatt Saigon", note: "Premium CBD ballrooms for corporate galas" },
  { name: "Aman Halong Bay (Heritage Bay)", note: "Private luxury cruise option for top-tier incentive groups" },
];

const FAQS = [
  {
    q: "Why is Vietnam rising as an incentive destination?",
    a: "Vietnam offers exceptional value with rapidly maturing 5-star inventory. International brands like Four Seasons, InterContinental, Sofitel and Aman have invested heavily in the past decade. For incentive groups looking for a fresh story — beyond the usual Bangkok or Bali — Vietnam delivers cultural depth, dramatic landscapes, and 30–40% better value than comparable Southeast Asian destinations.",
  },
  {
    q: "Does Trawelldmc operate multi-city Vietnam programmes?",
    a: "Yes — multi-city Vietnam (Hanoi to Ha Long Bay to Ho Chi Minh City, for example) is one of our most-requested formats. We handle internal flight blocks on Vietnam Airlines, hotel-to-hotel handovers, English-speaking guides at each city, and one named programme manager who travels with the group end-to-end.",
  },
  {
    q: "What is the best time of year for a Vietnam programme?",
    a: "October to April is the best window across most of Vietnam — dry, mild and comfortable. The north (Hanoi, Ha Long) is best November to April. The south (HCMC, Phu Quoc) is good year-round but driest December to March. We avoid May–September for Ha Long Bay due to humidity and storm risk.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function VietnamDmc() {
  return (
    <div data-testid="vietnam-dmc-page">
      <Seo
        path="/vietnam-dmc"
        title="DMC Vietnam — Incentive & Group Travel | Trawelldmc"
        description="Plan your Vietnam incentive or corporate trip with Trawelldmc. Hanoi, Ho Chi Minh City & Ha Long Bay group travel experts. 20+ years experience."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.vietnam}
          alt="Vietnam incentive and group travel — Trawelldmc DMC Vietnam"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Vietnam DMC · Hanoi · HCMC · Ha Long Bay
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Vietnam</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Incentive and group travel across Vietnam's most photogenic
              destinations — engineered by a team with two decades on the
              ground.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?destination=Vietnam" className="btn-primary" data-testid="vn-cta-proposal">
                Get a Vietnam proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="vn-cta-ai-quote">Instant AI estimate</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Vietnam</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Southeast Asia's <br />fastest-rising incentive destination.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Vietnam has emerged as Southeast Asia's most exciting new
              incentive destination. Over the past decade, international hotel
              brands have invested heavily — Four Seasons, InterContinental,
              Aman, Sofitel and Park Hyatt now anchor a 5-star inventory that
              rivals Thailand and Singapore. Combined with dramatic landscapes,
              French colonial heritage, and a fast-improving aviation network,
              Vietnam delivers programmes that feel fresh, photogenic and
              memorable.
            </p>
            <p>
              Hanoi is the cultural anchor. The restored Sofitel Legend
              Metropole — a 1901 colonial landmark — sits at the centre of a
              city that combines Old Quarter charm with serious 5-star
              inventory. Capella Hanoi, Hilton Opera House and the new
              waterfront properties round out the offering. For incentive
              groups with a cultural narrative, Hanoi is unmatched.
            </p>
            <p>
              Ho Chi Minh City is the commercial heart. Park Hyatt Saigon, The
              Reverie and Sofitel Plaza Saigon offer premium ballrooms and
              rooftop venues. The city's energy and depth of dining make it
              perfect for short, high-impact corporate offsites. Ha Long Bay
              brings the wow factor — UNESCO-listed karst formations across
              1,600 islands, with private junk-boat charters that turn a
              dinner into a story your delegates will tell for years.
            </p>
            <p>
              Trawelldmc has operated Vietnam for two decades. Our local team
              owns every venue contract, transfer logistic and F&B
              specification. From visa documentation to in-flight catering on
              private cruise boats, we don't outsource what matters.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Where we work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Our Vietnam footprint.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CITIES.map((c) => (
              <div key={c.name} className="card-flat p-8">
                <MapPin className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{c.name}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Our Vietnam DMC services.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8">
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{s.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br /><span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Frequently Asked Questions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`vn-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Interested in <span className="text-[#FF5238]">Vietnam?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Let Trawelldmc handle everything. Request a proposal today —
              our team responds within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?destination=Vietnam" className="btn-primary w-full justify-center" data-testid="vn-bottom-cta">
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Related DMC destination pages">
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="vn-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="vn-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/philippines-dmc" className="group flex items-start gap-3" data-testid="vn-link-philippines">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Philippines</div>
                <div className="text-sm text-slate-500 mt-1">Manila · Cebu · Palawan</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="vn-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
